import { useQuery, useMutation } from 'react-query';
import { useClient } from '../../../../api/api-client';

export function useGetRootNodes(queryConfig = {}) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  const GET_ROOT_NODE = 'get_root_nodes';
  return useQuery({
    queryKey: ['get_root_nodes', deskName],
    queryFn: () =>
      client(
        `helpdesk/dashboard/node?option=${GET_ROOT_NODE}&desk_name=${deskName}`,
      ),
    ...queryConfig,
  });
}

export function useGetChildNodes(nodeId, queryConfig = {}, node) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  return useQuery({
    queryKey: ['get_children', nodeId],
    queryFn: () =>
      client(
        `helpdesk/dashboard/node?option=get_children&parent_id=${nodeId}&desk_name=${deskName}`,
      ),
    ...queryConfig,
    enabled: Boolean(nodeId && node?.has_children),
  });
}

export function useGetCreateNodeFields(
  formtype = null,
  parentId,
  nodeNature,
  queryConfig = {},
) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  return useQuery({
    queryKey: ['get_create_form', formtype, parentId],
    queryFn: () =>
      client(
        `helpdesk/dashboard/get_create_form?form_type=${nodeNature}&parent_id=${
          parentId ? parentId : ''
        }&desk_name=${deskName}`,
      ),
    ...queryConfig,
    enabled: Boolean(formtype === 1 && nodeNature),
  });
}

export function useGetEditNodeFields(
  formtype = null,
  nodeId,
  queryConfig = {},
) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  const queryInfo = useQuery({
    queryKey: ['get_edit_form', formtype, nodeId],
    queryFn: () =>
      client(
        `helpdesk/dashboard/get_edit_form?node_id=${nodeId}&desk_name=${deskName}`,
      ),
    ...queryConfig,
    enabled: Boolean(formtype === 2 && nodeId),
    cacheTime: 0,
  });

  return {
    ...queryInfo,
    data: {
      form_data: queryInfo.data?.form_data.map((field) => {
        if (queryInfo.data?.rule_list?.includes(field.id)) {
          let ids = [];
          for (let i = 0; i < field.value.length; i++) {
            ids = [...ids, field.value[i].id];
          }
          field.rule_ids_values = ids;
        }
        return field;
      }),
      rule_list: queryInfo.data?.rule_list,
      path: queryInfo.data?.path,
    },
  };
}

export function useCreateNode(mutationConfig, refetchRootNodes) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  return useMutation({
    mutationKey: [],
    mutationFn: (data) => {
      data.desk_name = deskName;
      return client(`helpdesk/dashboard/node`, { data }).then(() => {
        refetchRootNodes();
      });
    },
    ...mutationConfig,
  });
}

export function useDeleteNode(mutationConfig, refetchRootNodes) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  return useMutation({
    mutationKey: [],
    mutationFn: (nodeId) => {
      const data = { desk_name: deskName };
      return client(`helpdesk/dashboard/node?node_id=${nodeId}`, {
        method: 'DELETE',
        data: data,
      }).then(() => {
        refetchRootNodes();
      });
    },
    ...mutationConfig,
  });
}

export function useDuplicateNode(mutationConfig, refetchRootNodes) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  return useMutation({
    mutationKey: [],
    mutationFn: (nodeId) => {
      const data = { desk_name: deskName };
      return client(`helpdesk/dashboard/node/duplicate?node_id=${nodeId}`, {
        data: data,
      }).then(() => {
        refetchRootNodes();
      });
    },
    ...mutationConfig,
  });
}

export function useUpdateNode(
  nodeId = null,
  mutationConfig = {},
  setRefetchParentEnable,
) {
  const client = useClient();
  const deskName = window.location.pathname.includes('RIDER_HELPDESK')
    ? 'rider'
    : 'customer';
  return useMutation({
    mutationKey: [nodeId],
    mutationFn: (data) => {
      data.desk_name = deskName;
      return client(`helpdesk/dashboard/node?node_id=${nodeId}`, {
        data: data,
        method: 'PUT',
      }).then((res) => {
        setRefetchParentEnable(true);
      });
    },
    ...mutationConfig,
    enabled: Boolean(nodeId),
  });
}
